import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useDarkMode } from "../../../context/ThemeContext";
import { useDebounce } from "../../../hooks/useDebounce";
import PaginationControls from "../../../components/base/PaginationControls";
import { useCuadreUnits } from "../../../features/sg/cuadreSource";
import { buildPlatformRows, conditionKeys, sumRows, type PlatformAccountRow, type PlatformTab } from "../../../features/sg/plataformas";
import { useRetransmissions } from "../../../features/sg/retransmissions";
import { platformSheet, TAB_LABEL } from "../../../features/sg/informes";
import { downloadXlsx } from "../../../features/sg/xlsx";
import { fileDate } from "../../../features/sg/exportRows";
import { timeAgo } from "../../../features/sg/format";
import { notifyError, notifyOk } from "../../../features/sg/notify";
import { PLATFORMS, PLATFORM_META } from "../../../features/sg/platforms";
import { PlatformLogo } from "../components/PlatformLogo";
import { StatusBadge } from "../components/cells";
import { fmt } from "../cuadre-section/ui";

const PER_PAGE = 50;
const TABS: PlatformTab[] = [...PLATFORMS, "none"];

type AccFilter = "retrans" | "notBillable" | "silent" | "inactive";
type Sort = "total" | "billable" | "silent" | "name";

const FILTERS: Record<AccFilter, (r: PlatformAccountRow) => boolean> = {
  retrans: (r) => !!r.retransmission?.length,
  notBillable: (r) => r.notBillable > 0,
  silent: (r) => r.silent > 0,
  inactive: (r) => r.inactive > 0,
};
const SORTS: Record<Sort, (a: PlatformAccountRow, b: PlatformAccountRow) => number> = {
  total: (a, b) => b.total - a.total,
  billable: (a, b) => b.billable - a.billable,
  silent: (a, b) => b.silent - a.silent,
  name: () => 0,
};

const norm = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Vista por plataforma: cada cuenta de SG (el «usuario», como Mapon identifica a
// la empresa) con sus unidades en esa plataforma. Estado en la URL, como el Cuadre.
export default function Plataformas() {
  const { t } = useTranslation("sg");
  const { isDarkMode } = useDarkMode();
  const queryClient = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const [exporting, setExporting] = useState(false);

  const tab: PlatformTab = TABS.includes(sp.get("p") as PlatformTab) ? (sp.get("p") as PlatformTab) : "v5";
  const accFilter = (sp.get("f") as AccFilter) || "";
  const condition = sp.get("etq") ?? "";
  const sort: Sort = (sp.get("orden") as Sort) || "total";
  const page = Math.max(1, Number(sp.get("pag")) || 1);
  const [search, setSearch] = useState(sp.get("q") ?? "");
  const q = useDebounce(search, 250);

  const { source, units, progress, resetProgress } = useCuadreUnits();
  const retrans = useRetransmissions();
  const accounts = source.data?.accounts;

  const byTab = useMemo(
    () => Object.fromEntries(TABS.map((p) => [p, accounts ? buildPlatformRows(units, accounts, p, retrans.data) : []])) as Record<PlatformTab, PlatformAccountRow[]>,
    [units, accounts, retrans.data]
  );
  const rows = byTab[tab];
  const conds = useMemo(() => conditionKeys(rows), [rows]);

  const searched = useMemo(() => {
    const nq = norm(q.trim());
    return nq ? rows.filter((r) => norm(`${r.name} ${r.company}`).includes(nq)) : rows;
  }, [rows, q]);
  // Conteo de cada filtro con la búsqueda y la etiqueta puestas.
  const withCond = useMemo(() => (condition ? searched.filter((r) => (r.conditions[condition] ?? 0) > 0) : searched), [searched, condition]);
  const filterCounts = useMemo(
    () => Object.fromEntries((Object.keys(FILTERS) as AccFilter[]).map((k) => [k, withCond.filter(FILTERS[k]).length])) as Record<AccFilter, number>,
    [withCond]
  );
  const filtered = useMemo(() => {
    const list = accFilter ? withCond.filter(FILTERS[accFilter]) : withCond;
    return [...list].sort((a, b) => SORTS[sort](a, b) || a.name.localeCompare(b.name, "es"));
  }, [withCond, accFilter, sort]);
  const totals = useMemo(() => sumRows(filtered), [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, totalPages);
  const pageRows = filtered.slice((current - 1) * PER_PAGE, current * PER_PAGE);

  const update = (patch: Record<string, string | null>, replace = false) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    }
    if (!("pag" in patch)) next.delete("pag");
    setSp(next, { replace });
  };

  async function exportView() {
    setExporting(true);
    try {
      const sheet = platformSheet(units, accounts ?? [], tab, retrans.data, new Set(filtered.map((r) => r.id)));
      await downloadXlsx(`plataforma_${TAB_LABEL[tab].toLowerCase().replace(/\s+/g, "_")}_${fileDate()}.xlsx`, sheet.name, sheet.headers, sheet.rows);
      notifyOk(t("plataformas.exported", { n: fmt(filtered.length) }));
    } catch (e: any) {
      notifyError(e.message);
    } finally {
      setExporting(false);
    }
  }

  const card = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const strong = isDarkMode ? "text-white" : "text-gray-900";
  const field = `h-10 px-3 rounded-xl border text-sm ${isDarkMode ? "bg-dark-card border-dark-border text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`;
  const retransUnknown = tab !== "v5" || retrans.isError;

  return (
    <div className="h-full min-h-0 w-full overflow-y-auto">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-5">
        <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold tracking-tight ${strong}`}>{t("plataformas.title")}</h1>
            <p className={`text-sm mt-1 ${muted}`}>
              {source.data ? t("plataformas.subtitleAgo", { ago: timeAgo(source.data.generatedAt) }) : t("plataformas.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportView}
              disabled={!source.data || exporting || !filtered.length}
              className={`h-9 px-3 rounded-xl border text-sm font-medium flex items-center gap-2 transition-transform active:scale-[0.97] disabled:opacity-50 ${
                isDarkMode ? "border-dark-border text-gray-200 hover:bg-dark-card" : "border-gray-200 text-gray-700 hover:bg-gray-50"
              }`}
            >
              <i className="ri-download-2-line" aria-hidden="true" />
              {exporting ? t("common.exporting") : t("cuadre.export")}
            </button>
            <button
              onClick={() => {
                resetProgress();
                queryClient.invalidateQueries({ queryKey: ["sg-cuadre-source"] });
                queryClient.invalidateQueries({ queryKey: ["sg-retransmissions"] });
              }}
              disabled={source.isFetching}
              title={t("cuadre.refresh")}
              aria-label={t("cuadre.refresh")}
              className={`w-9 h-9 rounded-xl border flex items-center justify-center transition-transform active:scale-95 disabled:opacity-50 ${
                isDarkMode ? "border-dark-border text-gray-300 hover:bg-dark-card" : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <i className={`ri-refresh-line ${source.isFetching ? "animate-spin" : ""}`} />
            </button>
          </div>
        </header>

        {source.isError ? (
          <div className={`rounded-2xl border p-6 text-sm text-red-500 ${card}`}>{(source.error as Error)?.message || t("cuadre.loadError")}</div>
        ) : !source.data ? (
          <div className={`rounded-2xl border p-12 flex flex-col items-center gap-3 text-sm ${card} ${muted}`}>
            <i className="ri-loader-4-line animate-spin text-2xl" />
            {t(`cuadre.loading.${progress.stage}`, { n: fmt(progress.n) })}
          </div>
        ) : (
          <>
            {/* Una tarjeta por plataforma: elegirla cambia la tabla */}
            <nav role="tablist" aria-label={t("plataformas.title")} className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {TABS.map((p) => {
                const s = sumRows(byTab[p]);
                const active = p === tab;
                const color = p === "none" ? "#9CA3AF" : PLATFORM_META[p].color;
                return (
                  <button
                    key={p}
                    role="tab"
                    aria-selected={active}
                    onClick={() => update({ p: p === "v5" ? null : p, f: null, etq: null })}
                    className={`relative text-left rounded-2xl border p-4 overflow-hidden transition-[border-color,background-color,transform] duration-150 active:scale-[0.98] ${
                      active
                        ? isDarkMode
                          ? "bg-dark-card border-primary-500/60"
                          : "bg-white border-primary-500 shadow-sm"
                        : isDarkMode
                          ? "bg-dark-surface border-dark-border hover:border-dark-hover"
                          : "bg-white border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className="absolute left-0 top-4 bottom-4 w-[3px] rounded-r-full" style={{ background: color }} aria-hidden="true" />
                    <span className="flex items-center gap-2">
                      {p === "none" ? <i className="ri-question-line text-gray-400 text-lg leading-none" aria-hidden="true" /> : <PlatformLogo platform={p} size={20} />}
                      <span className={`text-sm font-semibold ${strong}`}>{TAB_LABEL[p]}</span>
                    </span>
                    <span className={`mt-2 block text-3xl font-bold tabular-nums tracking-tight ${strong}`}>{fmt(s.total)}</span>
                    <span className={`block text-xs ${muted}`}>{t("plataformas.tabHint", { accounts: fmt(s.accounts), billable: fmt(s.billable) })}</span>
                  </button>
                );
              })}
            </nav>

            <div className={`rounded-2xl border ${card}`}>
              <div className={`p-4 border-b flex flex-col gap-3 ${isDarkMode ? "border-dark-border" : "border-gray-200"}`}>
                <div className="flex flex-col sm:flex-row gap-2">
                  <label className="relative flex-1">
                    <span className="sr-only">{t("plataformas.search")}</span>
                    <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm ${muted}`} aria-hidden="true" />
                    <input
                      type="search"
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        update({ q: e.target.value || null }, true);
                      }}
                      placeholder={t("plataformas.search")}
                      className={`w-full pl-9 ${field} placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    />
                  </label>
                  <select aria-label={t("plataformas.condition")} value={condition} onChange={(e) => update({ etq: e.target.value || null })} className={field}>
                    <option value="">{t("plataformas.anyCondition")}</option>
                    {conds.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <select aria-label={t("cuadre.sort")} value={sort} onChange={(e) => update({ orden: e.target.value === "total" ? null : e.target.value })} className={field}>
                    {(Object.keys(SORTS) as Sort[]).map((s) => (
                      <option key={s} value={s}>
                        {t(`plataformas.sorts.${s}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className={`tabular-nums font-semibold mr-1 ${strong}`}>{t("cuadre.countAccounts", { n: fmt(filtered.length) })}</span>
                  {(Object.keys(FILTERS) as AccFilter[])
                    .filter((k) => k !== "retrans" || !retransUnknown)
                    .map((k) => {
                      const active = accFilter === k;
                      return (
                        <button
                          key={k}
                          type="button"
                          aria-pressed={active}
                          disabled={!filterCounts[k] && !active}
                          onClick={() => update({ f: active ? null : k })}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-[background-color,transform] duration-150 active:scale-[0.97] disabled:opacity-40 ${
                            active
                              ? "bg-primary-500 text-black"
                              : isDarkMode
                                ? "bg-white/10 text-gray-200 hover:bg-white/15"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                        >
                          {t(`plataformas.filters.${k}`)}
                          <span className="tabular-nums opacity-70">{fmt(filterCounts[k])}</span>
                        </button>
                      );
                    })}
                  {retransUnknown && <span className={`text-xs ${muted}`}>{t(tab === "v5" ? "plataformas.retransUnavailable" : "plataformas.retransOnlyV5")}</span>}
                </div>
              </div>

              <div className="px-4 pb-4">
                {pageRows.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[980px] text-sm">
                      <thead>
                        <tr className={`border-b text-left text-[11px] uppercase tracking-wider ${isDarkMode ? "text-gray-400 border-dark-border" : "text-gray-500 border-gray-200"}`}>
                          <th className="py-2.5 pr-4 font-medium">{t("cuadre.cols.account")}</th>
                          {!retransUnknown && <th className="py-2.5 pr-4 font-medium">{t("plataformas.cols.retrans")}</th>}
                          <th className="py-2.5 pr-4 font-medium text-right">{t("plataformas.cols.total")}</th>
                          <th className="py-2.5 pr-4 font-medium text-right">{t("cuadre.billing.billable")}</th>
                          <th className="py-2.5 pr-4 font-medium text-right">{t("cuadre.billing.notBillable")}</th>
                          <th className="py-2.5 pr-4 font-medium text-right">{t("plataformas.cols.inactive")}</th>
                          <th className="py-2.5 pr-4 font-medium">{t("cuadre.cols.reporting")}</th>
                          {conds.map((c) => (
                            <th key={c} className="py-2.5 pr-4 font-medium text-right whitespace-nowrap">
                              {c}
                            </th>
                          ))}
                          <th className="py-2.5 w-20" aria-label={t("cuadre.cols.actions")} />
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.map((r) => (
                          <Row key={r.id} r={r} conds={conds} tab={tab} retransUnknown={retransUnknown} isDarkMode={isDarkMode} />
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className={`text-sm font-semibold ${strong}`}>
                          <td className="pt-3 pr-4">{t("plataformas.totalFiltered")}</td>
                          {!retransUnknown && <td className={`pt-3 pr-4 text-xs font-normal ${muted}`}>{t("plataformas.retransCount", { n: fmt(totals.retransmitting) })}</td>}
                          <td className="pt-3 pr-4 text-right tabular-nums">{fmt(totals.total)}</td>
                          <td className="pt-3 pr-4 text-right tabular-nums">{fmt(totals.billable)}</td>
                          <td className="pt-3 pr-4 text-right tabular-nums">{fmt(totals.notBillable)}</td>
                          <td className="pt-3 pr-4 text-right tabular-nums">{fmt(totals.inactive)}</td>
                          <td className="pt-3 pr-4 tabular-nums">
                            {fmt(totals.reporting)}/{fmt(totals.total)}
                          </td>
                          {conds.map((c) => (
                            <td key={c} className="pt-3 pr-4 text-right tabular-nums">
                              {fmt(filtered.reduce((n, r) => n + (r.conditions[c] ?? 0), 0))}
                            </td>
                          ))}
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className={`py-14 text-center text-sm ${muted}`}>
                    <i className="ri-filter-off-line text-2xl block mb-2" aria-hidden="true" />
                    {t("cuadre.empty")}
                    {(accFilter || condition || q) && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearch("");
                          update({ f: null, etq: null, q: null });
                        }}
                        className="block mx-auto mt-2 text-primary-500 hover:underline"
                      >
                        {t("cuadre.chips.clear")}
                      </button>
                    )}
                  </div>
                )}
                {totalPages > 1 && (
                  <PaginationControls currentPage={current} totalPages={totalPages} onPageChange={(p) => update({ pag: p > 1 ? String(p) : null })} />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ r, conds, tab, retransUnknown, isDarkMode }: { r: PlatformAccountRow; conds: string[]; tab: PlatformTab; retransUnknown: boolean; isDarkMode: boolean }) {
  const { t } = useTranslation("sg");
  const strong = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-500" : "text-gray-400";
  const num = (n: number, tone = strong) => (n ? <span className={`tabular-nums ${tone}`}>{fmt(n)}</span> : <span className={muted}>—</span>);
  const pct = r.total ? Math.round((r.reporting / r.total) * 100) : 0;
  const iconBtn = `inline-flex w-8 h-8 items-center justify-center rounded-lg transition-colors ${
    isDarkMode ? "text-gray-400 hover:bg-white/10 hover:text-white" : "text-gray-500 hover:bg-gray-100 hover:text-gray-900"
  }`;
  const cuadreLink = `/sg/cuadre?${new URLSearchParams({ ...(r.id ? { cuenta: String(r.id) } : {}), plat: tab }).toString()}`;
  return (
    <tr className={`border-b transition-colors ${isDarkMode ? "border-dark-border hover:bg-white/[0.03]" : "border-gray-100 hover:bg-gray-50"}`}>
      <td className="py-2.5 pr-4 max-w-[300px]">
        <div className={`truncate font-medium ${strong}`} title={r.name}>
          {r.name || t("cuadre.noAccount")}
        </div>
        {r.company && r.company !== r.name && (
          <div className={`truncate text-xs ${muted}`} title={r.company}>
            {r.company}
          </div>
        )}
        {r.id > 0 && (
          <div className="mt-0.5 flex flex-wrap gap-1">
            <StatusBadge label={t(r.billableAccount ? "cuadre.billing.billable" : "cuadre.billing.notBillable")} isDarkMode={isDarkMode} />
            {r.accountCondition && <StatusBadge label={r.accountCondition} isDarkMode={isDarkMode} />}
          </div>
        )}
      </td>
      {!retransUnknown && (
        <td className="py-2.5 pr-4 max-w-[200px]">
          {r.retransmission?.length ? (
            <span className="inline-flex flex-wrap gap-1">
              {r.retransmission.map((n) => (
                <span key={n} className={`whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-medium ring-1 ${isDarkMode ? "bg-sky-500/15 text-sky-300 ring-sky-500/30" : "bg-sky-50 text-sky-700 ring-sky-200"}`}>
                  {n}
                </span>
              ))}
            </span>
          ) : (
            <span className={muted}>—</span>
          )}
        </td>
      )}
      <td className="py-2.5 pr-4 text-right font-semibold">{num(r.total)}</td>
      <td className="py-2.5 pr-4 text-right">{num(r.billable)}</td>
      <td className="py-2.5 pr-4 text-right">{num(r.notBillable, "text-amber-500 font-medium")}</td>
      <td className="py-2.5 pr-4 text-right">{num(r.inactive)}</td>
      <td className="py-2.5 pr-4 min-w-[150px]">
        <div className="flex items-center gap-2">
          <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${isDarkMode ? "bg-white/10" : "bg-gray-100"}`}>
            <div className={`h-full rounded-full ${pct >= 90 ? "bg-emerald-500" : pct >= 60 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${pct}%` }} />
          </div>
          <span className={`tabular-nums text-xs w-16 text-right ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
            {fmt(r.reporting)}/{fmt(r.total)}
          </span>
        </div>
      </td>
      {conds.map((c) => (
        <td key={c} className="py-2.5 pr-4 text-right">
          {num(r.conditions[c] ?? 0)}
        </td>
      ))}
      <td className="py-2.5 text-right whitespace-nowrap">
        <Link to={cuadreLink} title={t("cuadre.actions.viewUnits")} aria-label={t("cuadre.actions.viewUnits")} className={iconBtn}>
          <i className="ri-list-unordered" />
        </Link>
        {r.id > 0 && (
          <Link to={`/sg/users/edit/${r.id}`} title={t("cuadre.actions.editAccount")} aria-label={t("cuadre.actions.editAccount")} className={iconBtn}>
            <i className="ri-pencil-line" />
          </Link>
        )}
      </td>
    </tr>
  );
}
